import { NavLink } from 'react-router';

import { Tooltip } from '@/components/ui/tooltip';
import { useCurrentUser } from '@/features/auth/auth-context';
import { cn } from '@/lib/utils/cn';

import {
  isNavItemVisible,
  primaryNavigation,
  secondaryNavigation,
  type NavItem,
} from './navigation';

interface SidebarNavProps {
  collapsed?: boolean;
  /** Called after a navigation link is activated (used to close the mobile drawer). */
  onNavigate?: () => void;
}

const itemBaseClassName =
  'group relative flex h-9 items-center gap-3 rounded-md px-2.5 text-sm font-medium transition-colors duration-150';

/** Collapsed rail: square 40px hit targets, centred, so icons get a uniform rhythm. */
const collapsedItemClassName = 'mx-auto size-10 justify-center px-0';

function SidebarNavItem({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate: (() => void) | undefined;
}) {
  const Icon = item.icon;

  if (item.status === 'planned') {
    return (
      <Tooltip content={`${item.label} · Próximamente`} disabled={!collapsed}>
        {/* Focusable (not `disabled`) so keyboard users can reach the tooltip; it performs no action. */}
        <button
          type="button"
          aria-disabled="true"
          aria-label={collapsed ? `${item.label} (próximamente)` : undefined}
          className={cn(
            itemBaseClassName,
            'w-full cursor-not-allowed text-left text-sidebar-muted/70',
            collapsed && collapsedItemClassName,
          )}
        >
          <Icon className="size-[1.125rem] shrink-0" aria-hidden="true" />
          {!collapsed && (
            <>
              <span className="truncate">{item.label}</span>
              <span className="ml-auto rounded bg-sidebar-accent px-1.5 py-px text-[0.625rem] font-medium tracking-wide text-sidebar-muted uppercase">
                Pronto
              </span>
            </>
          )}
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={item.label} disabled={!collapsed}>
      <NavLink
        to={item.to}
        onClick={onNavigate}
        aria-label={collapsed ? item.label : undefined}
        className={({ isActive }) =>
          cn(
            itemBaseClassName,
            // Active: soft primary surface + 3px indicator on the left edge.
            'before:absolute before:inset-y-1.5 before:-left-3 before:w-[3px] before:rounded-r-full before:bg-primary before:opacity-0 before:transition-opacity',
            isActive
              ? 'bg-sidebar-active text-primary before:opacity-100 [&>svg]:text-primary'
              : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground [&>svg]:text-sidebar-muted hover:[&>svg]:text-foreground',
            collapsed && collapsedItemClassName,
          )
        }
      >
        <Icon className="size-[1.125rem] shrink-0 transition-colors" aria-hidden="true" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    </Tooltip>
  );
}

export function SidebarNav({ collapsed = false, onNavigate }: SidebarNavProps) {
  const { profile } = useCurrentUser();
  const sections = primaryNavigation
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => isNavItemVisible(item, profile.role)),
    }))
    .filter((section) => section.items.length > 0);
  const secondaryItems = secondaryNavigation.filter((item) => isNavItemVisible(item, profile.role));

  return (
    <nav
      aria-label="Navegación principal"
      className={cn(
        'flex flex-1 flex-col overflow-y-auto px-3 py-4',
        collapsed ? 'gap-4' : 'gap-6',
      )}
    >
      {sections.map((section, index) => (
        <div key={section.label} className="space-y-1">
          {collapsed ? (
            // No divider above the first group: it sits right under the logo border.
            index > 0 && (
              <div className="mx-auto mb-4 h-px w-6 bg-sidebar-border" aria-hidden="true" />
            )
          ) : (
            <p className="px-2.5 pb-1.5 text-[0.6875rem] font-medium tracking-[0.08em] text-sidebar-muted uppercase">
              {section.label}
            </p>
          )}
          <ul className={collapsed ? 'space-y-2' : 'space-y-0.5'}>
            {section.items.map((item) => (
              <li key={item.to}>
                <SidebarNavItem item={item} collapsed={collapsed} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </div>
      ))}

      {secondaryItems.length > 0 && (
        <ul
          className={cn(
            'mt-auto border-t border-sidebar-border pt-4',
            collapsed ? 'space-y-2' : 'space-y-0.5',
          )}
        >
          {secondaryItems.map((item) => (
            <li key={item.to}>
              <SidebarNavItem item={item} collapsed={collapsed} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      )}
    </nav>
  );
}
